import { me, setAuthTokenToCookies } from 'api/auth'
import { API_CALL_SUCCESS } from 'actions/actionConstants'
import { USER_LOGIN } from 'actions/apiActionConstants'
import type { AppAction } from 'actions/types/ActionTypes'
import type { UserManager } from 'oidc-client-ts'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { setMonitoringUser } from 'services/userMonitoring'
import { markOidcSession } from './authMethod'

// Restores a v2 session whose access token expired while the tab was away but
// whose refresh token is still valid (e.g. a laptop reopened after the weekend).
// signinSilent() runs the refresh-token grant headlessly, so a hard page load
// that SSR-rendered anonymous can be recovered without a manual reload or a trip
// through the hosted login. Uses the coordinator's long-lived UserManager so its
// silent-renew re-arms and keeps mirroring the cookie after the restore. Mirrors
// completeOidcLogin: validate the fresh token via /me before persisting, then
// dispatch the standard USER_LOGIN.
export function recoverOidcSession(userManager: UserManager): AppAction {
  return async(dispatch, getState) => {
    const state = getState()
    const regionCode = state.geo.currentRegionCode
    const luxPlusEnabled = getIsLuxPlusEnabled(state)

    const user = await userManager.signinSilent()

    const accessToken = user?.access_token
    if (!accessToken) {
      throw new Error('OIDC silent sign-in returned no access token')
    }

    const account = await me(accessToken, regionCode, luxPlusEnabled)
    // initialiseUser early-returns for the OIDC path, so attribute the restored
    // agent to error monitoring here (as its loggedIn branch would otherwise).
    setMonitoringUser(account.memberId)

    setAuthTokenToCookies(accessToken)
    markOidcSession()

    dispatch({
      type: API_CALL_SUCCESS,
      api: USER_LOGIN,
      source: 'email',
      data: {
        account,
        accessToken,
        // A silent restore is a rehydration, not an interactive sign-in: keep
        // isSignIn false so post-login side effects (signin analytics, the
        // "earn your own tour" promo) don't fire as if the agent just logged in.
        isSignIn: false,
        authMethod: 'oidc',
      },
    })
  }
}
